import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Image, Modal, Stack, Text } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { getAssetUrl } from "@utils/getAssetUrl";
import { useAppNavigate } from "@/apps/global/navigate/navigate";
import { CategoryDTO, ProductListResponseDTO, UserApi } from "@utils/api/ec";
import { useLoadingStore } from "@/stores/useLoadingStores";
import "./EcSeriesProductPage.scss";
import { useUserEvent } from "@/apps/@hooks/useUserEvent";
import { useWaitingInPage } from "@/apps/@hooks/useWaitingInPage";

export default function EcSeriesProductPage() {
	const { navigateToEcProduct, navigateToEcHome } = useAppNavigate();
	const { id } = useParams();
	const { logPage, logEvent } = useUserEvent();
	const displayRemind = localStorage.getItem("ec_liff_display_remind");

	const [allProducts, setAllProducts] = useState<ProductListResponseDTO[]>([]);
	const [category, setCategory] = useState<CategoryDTO>();
	const [togglingMap, setTogglingMap] = useState<Record<number, boolean>>({});
	const [remindOpened, setRemindOpened] = useState(true);
	const [isPageReady, setIsPageReady] = useState(false);

	const { setIsLoading } = useLoadingStore();

	const getCategory = async (categoryId: number) => {
		if (!categoryId) {
			navigateToEcHome();
			return;
		}
		setIsLoading(true);
		try {
			const [res] = await Promise.all([UserApi.getCategory(categoryId)]);
			if (!res.data.category.id) {
				navigateToEcHome();
				return;
			}
			setAllProducts(res.data.items);
			setCategory(res.data.category);
		} catch (err) {
			console.error("取得商品列表失敗", err);
		} finally {
			setIsPageReady(true);
			setIsLoading(false);
		}
	};

	const handleToggleCollection = async (
		productId: number,
		nextStatus: boolean,
	) => {
		if (togglingMap[productId]) {
			return;
		}

		try {
			if (nextStatus) {
				logEvent({
					category: "cta",
					path: `/productlist/category_id/${id}`,
					action: "like",
					event: "cta_like",
					value: productId.toString(),
				});
			} else {
				logEvent({
					category: "cta",
					path: `/productlist/category_id/${id}`,
					action: "unlike",
					event: "cta_unlike",
					value: productId.toString(),
				});
			}
			setTogglingMap((prev) => ({ ...prev, [productId]: true }));
			await UserApi.toggleProductCollection(productId, nextStatus);
			setTogglingMap((prev) => ({ ...prev, [productId]: false }));

			setAllProducts((prev) =>
				prev.map((it) =>
					it.id === productId ? { ...it, collection_status: nextStatus } : it,
				),
			);
		} catch (error) {
			console.error("切換收藏狀態失敗", error);
		}
	};

	const resolveAssetUrl = (path?: string): string => {
		if (!path) {
			return "";
		}
		if (/^(https?:)?\/\//.test(path)) return path;
		return getAssetUrl(path);
	};

	useEffect(() => {
		if (!id) {
			navigateToEcHome();
			return;
		}
		getCategory(Number(id));
	}, []);

	useEffect(() => {
		logPage({
			path: `/productlist/category_id/${id}`,
		});
	}, []);

	useWaitingInPage({
		enabled: isPageReady, // true / false
		getPath: () => `/productlist/category_id/${id}`,
		logEvent,
	});

	return (
		<Stack gap="6.19vw" flex={1}>
			<>
				<Stack justify="center" align="center" gap="2.84vw">
					{Number(id) === 6 ? (
						<Stack gap="0">
							<div
								style={{
									width: "100%",
									height: "57vw",
									overflow: "hidden",
									backgroundColor: "#000",
								}}
							>
								<video
									playsInline
									muted
									autoPlay
									loop
									preload="metadata"
									style={{
										width: "100%",
										height: "100%",
										display: "block",
										objectFit: "cover",
									}}
								>
									<source
										src={getAssetUrl("/videos/gemini-series.mp4")}
										type="video/mp4"
									/>
								</video>
							</div>
							<Image
								src={getAssetUrl(
									"/images/ec-campaign/product-page/gemini-title.jpg",
								)}
								alt="gemini-series-title"
							/>
						</Stack>
					) : (
						<Image
							src={resolveAssetUrl(`${category?.banner_url}`)}
							alt="logo"
						/>
					)}
				</Stack>

				<Stack
					// pb="14.95vw"
					gap="2.58vw"
				>
					<Text size="3.09vw" ta="center" fw="500">
						點擊愛心，將商品加入最愛清單
					</Text>
					<div className="ec-home-products">
						{allProducts.map((p, idx) => (
							<Stack
								key={`${p.name}-${idx}`}
								className="ec-home-product"
								pos="relative"
								gap="2.58vw"
							>
								<Image
									src={getAssetUrl(
										p.collection_status
											? "/images/ec-campaign/active-love-icon.png"
											: "/images/ec-campaign/love-icon.png",
									)}
									alt="love"
									pos="absolute"
									h="3.61vw"
									w="4.12vw"
									right="3.1vw"
									top="3.1vw"
									fit="contain"
									style={{
										zIndex: 1,
										cursor: togglingMap[p.id] ? "not-allowed" : "pointer",
										opacity: togglingMap[p.id] ? 0.6 : 1,
										display: "block",
									}}
									onClick={(e) => {
										e.stopPropagation();
										handleToggleCollection(p.id, !p.collection_status);
									}}
								/>

								<Carousel
									withIndicators
									withControls={false}
									classNames={{ indicator: "indicator" }}
									onClick={() => {
										logEvent({
											category: "cta",
											path: `/productlist/category_id/${id}`,
											action: "view",
											event: "cta_view",
											value: p.id.toString(),
										});
										navigateToEcProduct(p.id);
									}}
								>
									{p.images_url.map((url, imgIdx) => (
										<Carousel.Slide key={imgIdx}>
											<Image src={url} alt={p.name} h="56.96vw" />
										</Carousel.Slide>
									))}
								</Carousel>

								<Stack gap="1.03vw" ml="2.84vw">
									<Text size="3.09vw">{p.name}</Text>
									<Text c="#6C717A" size="2.58vw">
										${p.price.toLocaleString()}
									</Text>
								</Stack>
							</Stack>
						))}
					</div>
				</Stack>
				{displayRemind && (
					<Modal
						opened={remindOpened}
						onClose={() => {
							setRemindOpened(false);
							localStorage.removeItem("ec_liff_display_remind");
						}}
						centered
						withCloseButton={false}
						padding={0}
						size="auto"
						styles={{
							content: {
								width: "66.49vw",
								height: "46.91vw",
								padding: 0,
								borderRadius: 0,
								overflow: "hidden",
								background: "transparent",
							},
							body: {
								width: "100%",
								height: "100%",
								padding: 0,
							},
						}}
					>
						<div
							style={{ position: "relative", width: "100%", height: "100%" }}
						>
							<Modal.CloseButton
								onClick={() => {
									setRemindOpened(false);
									localStorage.removeItem("ec_liff_display_remind");
								}}
								style={{
									position: "absolute",
									top: "10px",
									right: "10px",
									zIndex: 2,
								}}
							/>

							<Image
								src={getAssetUrl(
									"/images/ec-campaign/series-product-page/collect-list-remind.png",
								)}
								alt="collect-list-remind"
								w="100%"
								h="100%"
								fit="contain"
							/>
						</div>
					</Modal>
				)}
			</>
			{/* )} */}
		</Stack>
	);
}
